import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Users, Video, Send, Loader2, CalendarClock } from 'lucide-react';
import { PostCard } from '@/components/legacy/LegacyChannel';

const API_URL = window.location.origin;

export const WitnessCircle = ({ user }) => {
  const [data, setData] = useState(null);
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchCircle = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/witness-circle/mine`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to load witness circle:', error);
      setData({ circle: null, posts: [] });
    }
  };
  useEffect(() => { fetchCircle(); }, []);

  const handlePost = async () => {
    if (!content.trim()) { toast.error('Write something first'); return; }
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/legacy/witness-circle/posts`, { content });
      setContent('');
      fetchCircle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  if (!data) return <div className="h-64 bg-sand rounded-xl animate-pulse" />;

  if (!data.circle) {
    return (
      <Card className="card-base" data-testid="witness-circle-empty">
        <CardContent className="p-10 text-center">
          <Users className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="font-heading text-lg text-forest">Your Witness Circle is being formed</p>
          <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
            Crystal and Dr. Nicole are thoughtfully placing each woman into a small circle of sisters
            who will walk closely with her this year. You'll be notified the moment yours is ready.
          </p>
        </CardContent>
      </Card>
    );
  }

  const circle = data.circle;

  return (
    <div className="space-y-6" data-testid="witness-circle">
      <Card className="card-base border-forest/20 bg-gradient-to-br from-forest/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-5 w-5 text-terracotta" />
            <p className="text-xs uppercase tracking-widest text-terracotta">My Witness Circle</p>
          </div>
          <h3 className="font-heading text-2xl text-forest" data-testid="witness-circle-name">{circle.name}</h3>
          {circle.description && <p className="text-sm text-gray-600 mt-1">{circle.description}</p>}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            <div className="flex -space-x-2">
              {(circle.members || []).map(m => (
                <Avatar key={m.id} className="h-9 w-9 border-2 border-white" title={m.full_name}>
                  <AvatarImage src={m.profile_image || undefined} />
                  <AvatarFallback className="bg-forest text-white text-xs">{m.full_name?.charAt(0)}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <p className="text-xs text-gray-500">{(circle.members || []).map(m => m.full_name?.split(' ')[0]).join(' · ')}</p>
          </div>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            {circle.meeting_rhythm && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <CalendarClock className="h-4 w-4 text-terracotta" /> {circle.meeting_rhythm}
              </span>
            )}
            {circle.zoom_link && (
              <a href={circle.zoom_link} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-forest hover:bg-forest/90 text-white" data-testid="witness-circle-zoom-btn">
                  <Video className="h-4 w-4 mr-2" /> Join on Zoom
                </Button>
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Composer */}
      <Card className="card-base">
        <CardContent className="p-4 space-y-3">
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Only your circle can see this. What's true for you right now?"
            rows={3} data-testid="wc-content-input" />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-gray-400">Private to your Witness Circle</p>
            <Button onClick={handlePost} disabled={posting} className="bg-forest hover:bg-forest/90 text-white" data-testid="wc-submit-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Share
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feed */}
      {data.posts.length > 0 ? (
        <div className="space-y-4">
          {data.posts.map(post => (
            <PostCard key={post.id} post={post} user={user} channel="circle" onRefresh={fetchCircle} />
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6">A quiet space, for now. Share the first word with your circle.</p>
      )}
    </div>
  );
};

export default WitnessCircle;
